// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { CircularProgress, Grow } from '@mui/material';
import { deepEqual as isEqual } from 'fast-equals';
import { type AssetsSearchResp } from '@shared/data-access/interfaces/api/ai/assets';
import { useLayoutEffect, useRef } from 'react';
import type { FormattedUniverse } from '@shared/data-access/interfaces/api/ai/screener';
import {
  type ETF,
  type MutualFund,
} from '@shared/data-access/interfaces/api/ai/screener';
import { useTranslation } from 'react-i18next';

interface ListSuggestionsProps {
  allTickers:
    | AssetsSearchResp[]
    | FormattedUniverse[]
    | ETF[]
    | MutualFund[]
    | null;
  handleSelectValue: (value: string) => void;
  isLoading: boolean;
  renderSuggestions: boolean;
  suggestionIndex: number;
  handleOverList: () => void;
  typeOfAssetSearch?: 'ticker' | 'universe';
}

export const ListSuggestions = ({
  allTickers,
  handleSelectValue,
  isLoading,
  renderSuggestions,
  suggestionIndex,
  handleOverList,
  typeOfAssetSearch = 'ticker',
}: ListSuggestionsProps) => {
  const searchResultRef = useRef<any>(null);
  const { t } = useTranslation();

  useLayoutEffect(() => {
    const scrollIntoView = (position: number) => {
      if (searchResultRef.current) {
        searchResultRef.current!.parentNode!.scrollTo({
          top: position * 40,
          behavior: 'smooth',
        });
      }
    };

    scrollIntoView(suggestionIndex);
  }, [suggestionIndex]);

  return (
    <Grow
      in={renderSuggestions}
      style={{ transformOrigin: '0 0 0' }}
      {...(renderSuggestions ? { timeout: 500 } : {})}
      data-testid="list-suggestions"
      className={`selector scroll absolute z-20 max-h-80 w-full overflow-y-scroll rounded-lg bg-page py-2 shadow-md`}
      onMouseEnter={handleOverList}
    >
      <div>
        {isLoading && (
          <div className="flex justify-center p-1" data-testid="loading">
            <CircularProgress size={20} />
          </div>
        )}

        {!allTickers && !isLoading && (
          <p className="text-center text-neutral-400" data-testid="type-ticker">
            {typeOfAssetSearch === 'ticker'
              ? t('list-suggestions.please-type-ticker', { ns: 'components' })
              : t('list-suggestions.please-type-universe', { ns: 'components' })}
          </p>
        )}
        <div
          className="flex flex-col items-center justify-center"
          ref={searchResultRef}
        >
          {allTickers?.map(({ name, ticker }, i) => (
            <button
              className={`m-1 block w-[96%] cursor-pointer rounded-md p-1 text-left transition-all duration-75 hover:bg-brand-teal/30 ${
                i === suggestionIndex && 'bg-brand-teal/30'
              }`}
              key={i}
              onClick={() => handleSelectValue(ticker)}
              type="button"
              tabIndex={0}
              data-testid={`button-${ticker}`}
            >
              {ticker} - {name}
            </button>
          ))}
        </div>

        {isEqual(allTickers, []) && !isLoading && (
          <p className="text-center text-neutral-400" data-testid="not-found">
            {typeOfAssetSearch === 'ticker'
              ? t('list-suggestions.not-ticker-found', { ns: 'components' })
              : t('list-suggestions.not-universe-found', { ns: 'components' })}
          </p>
        )}
      </div>
    </Grow>
  );
};
